/**
 * CFB ELO Rating Engine
 *
 * A chess-style ELO system adapted for college football with:
 * - Margin of victory adjustments
 * - Home field advantage
 * - Conference-based priors
 * - Rating deviation (uncertainty) tracking
 */

import {
  DEFAULT_PARAMS,
  CONFERENCE_ADJUSTMENTS,
} from "./eloConstants.js";

/**
 * Calculate expected win probability for team A against team B
 * @param {number} ratingA - Team A's rating
 * @param {number} ratingB - Team B's rating
 * @returns {number} - Probability (0-1) that team A wins
 */
export function expectedOutcome(ratingA, ratingB) {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
}

/**
 * Calculate margin of victory multiplier
 * Uses logarithmic scaling with a cap to prevent blowout gaming
 * @param {number} pointDiff - Absolute point differential
 * @param {number} marginWeight - Weight for MOV (0-1)
 * @param {number} maxMultiplier - Maximum multiplier cap
 * @returns {number} - Multiplier for K-factor
 */
export function marginMultiplier(pointDiff, marginWeight, maxMultiplier) {
  if (marginWeight === 0) return 1;

  // Logarithmic scaling: diminishing returns for larger margins
  // ln(1) = 0, ln(8) ≈ 2.08, ln(22) ≈ 3.09, ln(35) ≈ 3.56
  const logMultiplier = Math.log(Math.abs(pointDiff) + 1) * marginWeight;

  // Scale so that a 2-TD win (14 pts) gives roughly 1.3x with default settings
  const scaled = 1 + logMultiplier * 0.4;

  return Math.min(scaled, maxMultiplier);
}

/**
 * Get the effective K-factor for a game
 * @param {number} baseK - Base K-factor
 * @param {number} week - Week number
 * @param {boolean} isConferenceGame - Whether both teams are in same conference
 * @returns {number} - Adjusted K-factor
 */
export function getEffectiveK(baseK, week, isConferenceGame) {
  let k = baseK;

  // Early season: higher volatility (weeks 1-4)
  if (week <= 4) {
    k *= 1.3;
  }
  // Late season: more stable (weeks 12+)
  else if (week >= 12) {
    k *= 0.85;
  }

  // Conference games are slightly more informative
  if (isConferenceGame) {
    k *= 1.1;
  }

  return k;
}

/**
 * Calculate new ratings after a game
 * @param {number} ratingA - Team A's current rating
 * @param {number} ratingB - Team B's current rating
 * @param {number} scoreA - Team A's score
 * @param {number} scoreB - Team B's score
 * @param {object} options - Game context and parameters
 * @returns {object} - New ratings for both teams
 */
export function calculateNewRatings(ratingA, ratingB, scoreA, scoreB, options = {}) {
  const {
    baseK = DEFAULT_PARAMS.baseK,
    marginWeight = DEFAULT_PARAMS.marginWeight,
    maxMarginMultiplier = DEFAULT_PARAMS.maxMarginMultiplier,
    homeAdvantage = DEFAULT_PARAMS.homeAdvantage,
    week = 1,
    isConferenceGame = false,
    isNeutralSite = false,
    teamAIsHome = true,
  } = options;

  // Apply home field advantage (if not neutral site)
  let adjustedRatingA = ratingA;
  let adjustedRatingB = ratingB;

  if (!isNeutralSite) {
    if (teamAIsHome) {
      adjustedRatingA += homeAdvantage;
    } else {
      adjustedRatingB += homeAdvantage;
    }
  }

  // Calculate expected outcomes
  const expectedA = expectedOutcome(adjustedRatingA, adjustedRatingB);
  const expectedB = 1 - expectedA;

  // Actual outcome (1 for win, 0 for loss, 0.5 for tie)
  let actualA, actualB;
  if (scoreA > scoreB) {
    actualA = 1;
    actualB = 0;
  } else if (scoreB > scoreA) {
    actualA = 0;
    actualB = 1;
  } else {
    actualA = 0.5;
    actualB = 0.5;
  }

  // Get effective K-factor
  const k = getEffectiveK(baseK, week, isConferenceGame);

  // Apply margin of victory multiplier
  const pointDiff = Math.abs(scoreA - scoreB);
  const movMultiplier = marginMultiplier(pointDiff, marginWeight, maxMarginMultiplier);

  // Calculate rating changes
  const changeA = k * movMultiplier * (actualA - expectedA);
  const changeB = k * movMultiplier * (actualB - expectedB);

  return {
    newRatingA: ratingA + changeA,
    newRatingB: ratingB + changeB,
    changeA,
    changeB,
    expectedA,
    expectedB,
    movMultiplier,
  };
}

/**
 * Initialize team ratings with conference-based priors
 * @param {Array} teams - Array of team objects with conference property
 * @param {object} params - Rating parameters
 * @returns {Map} - Map of team name to rating object
 */
export function initializeRatings(teams, params = DEFAULT_PARAMS) {
  const ratings = new Map();

  teams.forEach((team) => {
    const conferenceAdj = CONFERENCE_ADJUSTMENTS[team.conference] || 0;
    const initialRating = params.baseRating + conferenceAdj * params.conferenceBias;

    ratings.set(team.school, {
      rating: initialRating,
      initialRating,
      rd: params.initialRD, // Rating deviation (uncertainty)
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
      conference: team.conference,
      color: team.color,
      altColor: team.altColor,
      logos: team.logos,
      history: [{ week: 0, rating: initialRating, rd: params.initialRD }],
    });
  });

  return ratings;
}

/**
 * Process a single game and update ratings
 * @param {Map} ratings - Current ratings map
 * @param {object} game - Game object
 * @param {object} params - Rating parameters
 * @returns {Map} - Updated ratings map
 */
export function processGame(ratings, game, params = DEFAULT_PARAMS) {
  const homeTeam = game.homeTeam;
  const awayTeam = game.awayTeam;

  // Get current ratings (or create default for unknown teams like FCS)
  let homeData = ratings.get(homeTeam);
  let awayData = ratings.get(awayTeam);

  // If a team isn't in our FBS list, create a temporary rating
  // This handles FBS vs FCS matchups
  if (!homeData) {
    homeData = {
      rating: params.baseRating + (CONFERENCE_ADJUSTMENTS.FCS || -200),
      rd: params.initialRD,
      gamesPlayed: 0,
      isFCS: true,
    };
  }
  if (!awayData) {
    awayData = {
      rating: params.baseRating + (CONFERENCE_ADJUSTMENTS.FCS || -200),
      rd: params.initialRD,
      gamesPlayed: 0,
      isFCS: true,
    };
  }

  const isConferenceGame =
    homeData.conference &&
    awayData.conference &&
    homeData.conference === awayData.conference;

  // Calculate new ratings
  const result = calculateNewRatings(
    homeData.rating,
    awayData.rating,
    game.homePoints,
    game.awayPoints,
    {
      ...params,
      week: game.week,
      isConferenceGame,
      isNeutralSite: game.neutralSite,
      teamAIsHome: true,
    }
  );

  // Update home team (if FBS)
  if (ratings.has(homeTeam)) {
    const updated = ratings.get(homeTeam);
    updated.rating = result.newRatingA;
    updated.rd = Math.max(params.minRD, updated.rd * params.rdDecay);
    updated.gamesPlayed++;
    if (game.homePoints > game.awayPoints) {
      updated.wins++;
    } else if (game.awayPoints > game.homePoints) {
      updated.losses++;
    }
    updated.history.push({
      week: game.week,
      rating: result.newRatingA,
      rd: updated.rd,
      opponent: awayTeam,
      result: game.homePoints > game.awayPoints ? "W" : "L",
      score: `${game.homePoints}-${game.awayPoints}`,
      change: result.changeA,
    });
  }

  // Update away team (if FBS)
  if (ratings.has(awayTeam)) {
    const updated = ratings.get(awayTeam);
    updated.rating = result.newRatingB;
    updated.rd = Math.max(params.minRD, updated.rd * params.rdDecay);
    updated.gamesPlayed++;
    if (game.awayPoints > game.homePoints) {
      updated.wins++;
    } else if (game.homePoints > game.awayPoints) {
      updated.losses++;
    }
    updated.history.push({
      week: game.week,
      rating: result.newRatingB,
      rd: updated.rd,
      opponent: homeTeam,
      result: game.awayPoints > game.homePoints ? "W" : "L",
      score: `${game.awayPoints}-${game.homePoints}`,
      change: result.changeB,
    });
  }

  return ratings;
}

/**
 * Run the full ELO calculation for a season
 * @param {Array} teams - Array of team objects
 * @param {Array} games - Array of game objects (should be sorted by date)
 * @param {object} params - Rating parameters
 * @returns {object} - Final ratings and week-by-week snapshots
 */
export function calculateSeasonElo(teams, games, params = DEFAULT_PARAMS) {
  // Initialize ratings
  let ratings = initializeRatings(teams, params);

  // Track snapshots at each week
  const weekSnapshots = new Map();
  weekSnapshots.set(0, getRankings(ratings));

  // Group games by week
  const gamesByWeek = new Map();
  games.forEach((game) => {
    const week = game.week;
    if (!gamesByWeek.has(week)) {
      gamesByWeek.set(week, []);
    }
    gamesByWeek.get(week).push(game);
  });

  // Process games week by week
  const weeks = Array.from(gamesByWeek.keys()).sort((a, b) => a - b);

  weeks.forEach((week) => {
    const weekGames = gamesByWeek.get(week);

    // Process each game in the week
    weekGames.forEach((game) => {
      ratings = processGame(ratings, game, params);
    });

    // Save snapshot after this week
    weekSnapshots.set(week, getRankings(ratings));
  });

  return {
    ratings,
    rankings: getRankings(ratings),
    weekSnapshots,
    totalWeeks: Math.max(...weeks),
  };
}

/**
 * Convert ratings map to sorted rankings array
 * @param {Map} ratings - Ratings map
 * @returns {Array} - Sorted array of team rankings
 */
export function getRankings(ratings) {
  return Array.from(ratings.entries())
    .map(([team, data]) => ({
      team,
      ...data,
    }))
    .sort((a, b) => b.rating - a.rating)
    .map((team, index) => ({
      ...team,
      rank: index + 1,
    }));
}

/**
 * Calculate correlation between our rankings and official rankings
 * @param {Array} ourRankings - Our ELO rankings
 * @param {Array} officialElo - Official API ELO ratings
 * @returns {object} - Correlation stats
 */
export function compareToOfficial(ourRankings, officialElo) {
  const officialMap = new Map(officialElo.map((t) => [t.team, t.elo]));

  const comparisons = ourRankings
    .filter((t) => officialMap.has(t.team))
    .map((t) => ({
      team: t.team,
      ourRating: t.rating,
      ourRank: t.rank,
      officialElo: officialMap.get(t.team),
    }))
    .sort((a, b) => b.officialElo - a.officialElo)
    .map((t, i) => ({
      ...t,
      officialRank: i + 1,
      rankDiff: t.ourRank - (i + 1),
    }));

  // Calculate Spearman rank correlation
  const n = comparisons.length;
  const sumDiffSquared = comparisons.reduce((sum, t) => sum + t.rankDiff * t.rankDiff, 0);
  const spearman = 1 - (6 * sumDiffSquared) / (n * (n * n - 1));

  return {
    comparisons,
    spearmanCorrelation: spearman,
    averageRankDiff: comparisons.reduce((s, t) => s + Math.abs(t.rankDiff), 0) / n,
  };
}
